import React from 'react';
import { Calendar as CalendarIcon, MapPin } from 'lucide-react';
import { Card, CardContent, Badge } from '../ui';
import EntityLogo from '../ui/EntityLogo';
import { parseLocalDate } from '../../lib/dateUtils';
import { cn } from '../../lib/utils';
import { etiquetaDivision } from '../../lib/division';
import { rejillaPorSede, SIN_SEDE } from '../../lib/rejillaSedes';
import { enlaceSeguro } from '../../lib/enlaceSeguro';
import { ESTATUS_JUEGO_LABELS as ESTATUS_LABELS, ESTATUS_JUEGO_STYLES as ESTATUS_STYLES } from '../../lib/estatusJuego';

const CATEGORY_PALETTE = [
  'border-l-amber-500', 'border-l-emerald-500', 'border-l-sky-500', 'border-l-violet-500',
  'border-l-rose-500', 'border-l-teal-500', 'border-l-orange-500', 'border-l-fuchsia-500',
];

const buildCategoryColors = (nombres) => {
  const uniq = Array.from(new Set(nombres.filter(Boolean))).sort();
  const map = {};
  uniq.forEach((nombre, i) => { map[nombre] = CATEGORY_PALETTE[i % CATEGORY_PALETTE.length]; });
  return map;
};

// La division del juego (categoria + genero): dos divisiones de U14 son dos colores.
const etiquetaJuego = (j) => (j.categoria_nombre ? etiquetaDivision({ nombre: j.categoria_nombre, rama: j.rama }) : '');

const fmtHora = (h) => (h ? h.slice(0, 5) : null);
const fmtFecha = (iso) => {
  const d = parseLocalDate(iso);
  return d ? d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : null;
};

// Tarjetas de encuentro de UNA jornada (juegos = filas de getRolJornada), un bloque por sede.
// `sedes` ([{ nombre, maps_url }], en el orden del torneo) pone orden y mapa; sin ella agrupa por el texto de `sede`.
export default function RolJornadaCards({ juegos = [], highlightName = null, onSelectJuego, sedes = [] }) {
  if (juegos.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          <CalendarIcon className="mx-auto mb-3 h-10 w-10 opacity-30" />
          <p className="text-sm">Esta jornada todavía no tiene partidos.</p>
        </CardContent>
      </Card>
    );
  }

  const fecha = fmtFecha(juegos.find((j) => j.fecha)?.fecha);
  const colors = buildCategoryColors(juegos.map(etiquetaJuego));

  const tarjetas = (lista) => (
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {lista.map((j) => {
          const finalizado = j.estatus === 'finalizado';
          const cancelado = j.estatus === 'cancelado';
          const mine = highlightName && (j.local_club === highlightName || j.visita_club === highlightName);
          return (
            <Card key={j.juego_id}
              onClick={onSelectJuego ? () => onSelectJuego(j.juego_id) : undefined}
              title={onSelectJuego ? 'Capturar marcador / editar' : undefined}
              className={cn('min-w-0 border-l-4', colors[etiquetaJuego(j)], mine && 'ring-1 ring-primary',
                onSelectJuego && 'cursor-pointer transition hover:ring-2 hover:ring-primary/40')}>
              <CardContent className="space-y-2 p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="min-w-0 text-[11px] font-medium text-muted-foreground">{etiquetaJuego(j)}{j.grupo ? ` · Grupo ${j.grupo}` : ''}</span>
                  {!finalizado && (
                    <Badge variant="outline" className={cn('text-[10px]', ESTATUS_STYLES[j.estatus])}>
                      {ESTATUS_LABELS[j.estatus] || 'Programado'}
                    </Badge>
                  )}
                </div>

                <div className={cn('flex items-center gap-2', cancelado && 'opacity-60')}>
                  <div className="flex min-w-0 flex-1 items-center gap-1.5">
                    <EntityLogo src={j.local_logo} name={j.local_club} className="h-7 w-7 shrink-0" />
                    <span className="truncate text-sm font-medium">{j.local_club}</span>
                  </div>
                  <div className="shrink-0 px-1 text-center">
                    {finalizado ? (
                      <span className="text-sm font-semibold tabular-nums">{j.marcador_local ?? 0} - {j.marcador_visita ?? 0}</span>
                    ) : (
                      <span className="text-xs text-muted-foreground">{cancelado ? '—' : 'vs'}</span>
                    )}
                  </div>
                  <div className="flex min-w-0 flex-1 items-center justify-end gap-1.5">
                    <span className="truncate text-right text-sm font-medium">{j.visita_club}</span>
                    <EntityLogo src={j.visita_logo} name={j.visita_club} className="h-7 w-7 shrink-0" />
                  </div>
                </div>

                <p className="text-center text-xs text-muted-foreground">
                  {[j.campo, fmtHora(j.hora)].filter(Boolean).join(' · ') || 'Por programar'}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>
  );

  const grupos = rejillaPorSede(juegos, { ordenSedes: (sedes || []).map((x) => x.nombre) }).sedes;
  // Sin ninguna sede conocida no hay nada que titular: sin encabezado "Sin sede".
  const soloSinSede = grupos.length === 1 && grupos[0].nombre === SIN_SEDE;

  return (
    <div className="space-y-3">
      {fecha && (
        <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
          <span className="flex items-center gap-1"><CalendarIcon className="h-4 w-4" /> {fecha}</span>
        </div>
      )}

      {grupos.map((g) => {
        const delaSede = juegos.filter((j) => (j.sede || SIN_SEDE) === g.nombre);
        const mapa = enlaceSeguro(((sedes || []).find((x) => x.nombre === g.nombre) || {}).maps_url);
        return (
          <section key={g.nombre} className="min-w-0 space-y-2">
            {!soloSinSede && (
            <h3 className="flex items-center gap-2 text-sm font-semibold">
              <MapPin className="h-4 w-4" /> {g.nombre}
              {mapa && <a href={mapa} target="_blank" rel="noopener noreferrer" className="text-xs font-normal text-primary underline">mapa</a>}
            </h3>
            )}
            {tarjetas(delaSede)}
          </section>
        );
      })}

      {Object.keys(colors).length > 1 && (
        <div className="flex flex-wrap items-center gap-3">
          {Object.entries(colors).map(([nombre, cls]) => (
            <span key={nombre} className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span className={cn('h-3 w-3 rounded-sm border-l-4', cls)} />
              {nombre}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
